/*
Replay of a finished game — the route, not the view (owner's ask, 2026-09-07).

The replay used to be a view of its own: the game's panels, but under a bar, a seat picker and a
set of exits that were a copy of the game view's. Now the game view *is* the replay, in review
mode: the round clock gives way to the stepper, the hands go, a seat picker appears, and the
board is whichever snapshot is chosen (`hooks/useReplay`). A game that has just ended flips
into the same mode in place, so what a player sees when the war is over and what they see
opening it from the history a week later is one screen.

What is left here is what a route has to do before that view can mount: read
`GET /game/{id}/steps/`, which serves a FINISHED game's snapshots unredacted plus the seats and
the result, say so while it loads or when there is nothing to show, put the first snapshot in
the store — the game view reads the game *out of* the store, and there is none yet — and hand
the data over so the view does not fetch it twice. Only ticks whose snapshot survived the
rolling retention window are playable: a long game replays its last rounds, not its whole life.
*/

import { useEffect, useMemo, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import apiClient from "src/apiClient"
import BaseLayout from "src/layouts/BaseLayout"
import Game from "src/views/Game"
import { leaveGame, selectGame, setGame, setSpectatingAs } from "src/store/game"
import { gameFromStep, playableOf } from "src/utils/replay"

import "./Replay.css"

const Replay = () => {
  const { gameId } = useParams()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const game = useSelector(selectGame)
  const [data, setData] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    apiClient
      .get(`/game/${gameId}/steps/`)
      .then((response) => !cancelled && setData(response.data))
      .catch(() => !cancelled && setFailed(true))
    return () => {
      cancelled = true
    }
  }, [gameId])

  const playable = useMemo(() => playableOf(data), [data])

  // The first snapshot goes in so the game view has a game to mount on; the review hook inside
  // it takes over from there.
  useEffect(() => {
    if (data && playable.length) dispatch(setGame([gameFromStep(data, playable[0])]))
  }, [dispatch, data, playable])

  /* Leaving hands the store back. Without this the borrowed seat outlives the replay — the next
     live game would render through somebody else's eyes, which is the one way this feature could
     reach a screen it has no business on. */
  useEffect(
    () => () => {
      dispatch(setSpectatingAs(null))
      dispatch(leaveGame())
    },
    [dispatch]
  )

  if (failed || (data && playable.length === 0)) {
    return (
      <BaseLayout>
        <div className="replay-empty">
          <p>{failed ? t("replay.notFound") : t("replay.agedOut")}</p>
          <Link to="/">{t("replay.back")}</Link>
        </div>
      </BaseLayout>
    )
  }

  if (!data || !game || String(game.id) !== String(data.game_id)) {
    return (
      <BaseLayout>
        <div className="replay-empty">
          <p>{t("replay.loading")}</p>
        </div>
      </BaseLayout>
    )
  }

  return <Game review={{ fromHistory: true, preloaded: data }} />
}

export default Replay
